import React from "react";
import ReactDOM from "react-dom/client";
import { TimelineEngine } from "./core/TimelineEngine";
import { ProgressTracker } from "./core/ProgressTracker";
import { CommandExecutor } from "./core/CommandExecutor";
import { AudioController } from "./core/AudioController";
import { SyncManager } from "./core/SyncManager";
import { OrchestratorClient } from "./core/OrchestratorClient";
import { getTemplate } from "./templates/registry";
import { CanvasRoot } from "./renderer/CanvasRoot";
import { LayerManager } from "./renderer/LayerManager";
import { PlayerControls } from "./ui/PlayerControls";
import { NarrationPanel } from "./ui/NarrationPanel";
import { TopicInput } from "./ui/TopicInput";
import { StoryboardEditor } from "./ui/StoryboardEditor";
import type { Flow, FlowNode, VisualStyle } from "@learnengine/shared-types";
import { DEFAULT_VISUAL_STYLE } from "@learnengine/shared-types";
import { getStyleSpec } from "./style";
import { SkinProvider } from "./skins/SkinContext";
import { getSkin } from "./skins/skins";
import type { SkinId } from "./skins/types";

const SKIN_OPTIONS: { id: SkinId; label: string }[] = [
  { id: "studio", label: "Studio" },
  { id: "handdrawn", label: "Hand-drawn" },
  { id: "flat", label: "Flat" },
  { id: "doodle", label: "Doodle" },
];
import { buildDemoFlow } from "./demoFlow";
import { Phase1Preview } from "./v4/Preview";
import { Landing, LegalPage } from "./v4/Landing";
import { computeSceneMotion } from "./core/sceneMotion";
import { speak, cancelSpeech, pauseSpeech, resumeSpeech, isSpeechSupported, estimateSpeechSeconds } from "./core/SpeechController";
import "./app.css";

// Stretch each scene so the spoken narration isn't cut off (scenes loop, so a
// longer duration just loops the animation). Nodes without narration keep theirs.
function withVoiceTiming(flow: Flow): Flow {
  const nodes = flow.nodes.map((n) => {
    if (!n.narrationText) return n;
    const speech = estimateSpeechSeconds(n.narrationText);
    const dur = Math.max(n.durationSeconds, Math.min(30, Math.round(speech + 1.5)));
    return dur === n.durationSeconds ? n : { ...n, durationSeconds: dur };
  });
  const total = nodes.reduce((s, n) => s + n.durationSeconds, 0);
  return { ...flow, nodes, metadata: { ...flow.metadata, estimatedDurationSeconds: total } };
}

// ─── View states ─────────────────────────────────────────────────────
type AppView = "landing" | "loading" | "storyboard" | "playing" | "error";

// ─── Error Boundary ──────────────────────────────────────────────────
interface ErrorBoundaryState { hasError: boolean; error: Error | null }
class ErrorBoundary extends React.Component<{ children: React.ReactNode }, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };
  static getDerivedStateFromError(error: Error) { return { hasError: true, error }; }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ color: "#f1f5f9", fontFamily: "var(--font, sans-serif)", padding: "2rem", whiteSpace: "pre-wrap", background: "#0f172a", minHeight: "100vh" }}>
          <h2 style={{ color: "#f87171" }}>Anivaze — something broke while rendering</h2>
          <p>{this.state.error?.message}</p>
          <button onClick={() => location.reload()} style={{ margin: "12px 0", background: "#6366f1", color: "#fff", border: "none", borderRadius: 8, padding: "10px 16px", fontSize: 14, cursor: "pointer" }}>
            Reload the app
          </button>
          <details>
            <summary style={{ cursor: "pointer", color: "#94a3b8" }}>Technical details for a bug report</summary>
            <pre style={{ fontSize: "12px", opacity: 0.7 }}>{this.state.error?.stack}</pre>
          </details>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── API Configuration ──────────────────────────────────────────────
const API_BASE_URL =
  import.meta.env.VITE_ORCHESTRATOR_BASE_URL ||
  "https://learnengine-orchestrator-api.obedpaul-p.workers.dev";
const API_USER_ID = import.meta.env.VITE_USER_ID || "anonymous";

// ─── Helper: compute which node is active at a given time ────────────
function getNodeAtTime(
  flow: Flow,
  t: number,
): { node: FlowNode; index: number; localTime: number } | null {
  let acc = 0;
  for (let i = 0; i < flow.nodes.length; i++) {
    const node = flow.nodes[i];
    if (t >= acc && t < acc + node.durationSeconds) {
      return { node, index: i, localTime: t - acc };
    }
    acc += node.durationSeconds;
  }
  return null;
}

// ─── Template prop builder: maps node + localTime → template props ───
function buildTemplateProps(
  node: FlowNode,
  localTime: number,
): Record<string, unknown> {
  const props: Record<string, unknown> = {
    opacity: 1,
    // Anivaze v2: universal animation clock — seconds since this scene started.
    // Templates animate as a pure function of `t` (see ANIMATION-PATTERN.md).
    t: localTime,
    ...(node.props ?? {}),
  };

  // Collect fired actions (those whose `at` time has passed)
  const firedActions = node.actions.filter((a) => {
    const at = typeof a.at === "number" ? a.at : 0;
    return localTime >= at;
  });

  // Find the latest highlight target
  const highlights = firedActions.filter((a) => a.type === "highlight");
  const latestHighlight =
    highlights.length > 0 ? highlights[highlights.length - 1] : null;
  const highlightTarget = (latestHighlight as any)?.targetId ?? (latestHighlight as any)?.target;
  if (highlightTarget) {
    props.highlightTarget = highlightTarget;
  }

  // Fade
  const fadeAction = firedActions.find((a) => a.type === "fade");
  if (fadeAction) {
    const fadeAt = typeof fadeAction.at === "number" ? fadeAction.at : 0;
    const fadeDur = 0.8;
    const fadeProgress = Math.min(1, (localTime - fadeAt) / fadeDur);
    props.opacity = 1 - fadeProgress * 0.45;
  }

  // Template-specific dynamic props
  switch (node.template) {
    case "cylinder":
      props.pistonPosition = 0.3 + Math.sin(localTime * 0.8) * 0.3;
      if (firedActions.some((a) => a.type === "move")) {
        props.pistonPosition = 0.2 + Math.sin(localTime * 1.2) * 0.4;
      }
      if (latestHighlight?.target === "intake_valve") {
        props.openValves = ["intake_valve"];
      } else if (latestHighlight?.target === "exhaust_valve") {
        props.openValves = ["exhaust_valve"];
      }
      if (firedActions.some((a) => a.type === "combustion")) {
        props.combustion = true;
      }
      break;

    case "timeline_marker": {
      const advances = firedActions.filter((a) => a.type === "advance");
      const markersArr = (props.markers as string[]) || ["Step 1", "Step 2", "Step 3"];
      const activeIdx = Math.min(advances.length, markersArr.length - 1);
      props.activeIndex = activeIdx;
      props.completedIndices = Array.from({ length: activeIdx }, (_, i) => i);
      break;
    }

    case "box_flow":
    case "linear_pipeline":
    case "parallel_process":
    case "flow_chart":
    case "network_graph":
    case "neural_network":
    case "block_chain":
    case "circuit_board":
    case "server_rack":
    case "route_path":
    case "ocean_current":
    case "river_delta":
    case "trade_route":
    case "nuclear_reactor":
    case "hydroelectric_dam":
    case "power_grid_diagram":
    case "distillation_setup":
    case "electrochemistry_cell":
    case "leaf_photosynthesis":
    case "aqueduct_flow":
      if (firedActions.some((a) => a.type === "animate_flow")) {
        props.flowProgress = (localTime % 3) / 3;
      }
      props.flowProgress = (localTime % 4) / 4;
      break;

    case "circular_cycle":
    case "spiral_path":
    case "accordion":
    case "waterfall_steps":
    case "funnel_diagram":
    case "radial_burst":
    case "concentric_rings":
    case "pyramid_layers":
    case "arrow_cycle":
    case "people_group":
    case "ocean_layers":
    case "ecosystem_web":
    case "rock_strata":
    case "weather_system":
    case "forest_canopy":
    case "food_pyramid_diagram":
    case "water_cycle_diagram":
    case "traffic_light":
    case "era_timeline":
    case "color_spectrum":
    case "digestive_tract":
    case "moon_phases":
    case "star_lifecycle":
    case "org_hierarchy":
    case "kanban_board":
    case "swot_matrix":
    case "calendar_schedule":
    case "ear_anatomy":
    case "geometric_shapes":
    case "lens_optics":
    case "butterfly_lifecycle":
    case "ant_colony":
    case "frog_metamorphosis":
    case "flame_fire":
    case "periodic_table_view":
    case "chemical_bond":
    case "ph_scale":
    case "tree_growth_rings":
    case "seed_sprout":
    case "fruit_stages":
    case "bridge_forces":
    case "arch_physics":
    case "family_group":
    case "team_circle":
    case "teacher_student":
    case "story_panel":
    case "narrative_arc":
    case "book_page_turn":
    case "montage_grid":
    case "infographic_timeline":
    case "kitchen_scene":
    case "classroom_scene":
    case "shopping_cart":
    case "dining_table":
    case "jigsaw_puzzle":
    case "chess_board":
    case "card_deck":
    case "board_game":
    case "word_search":
    case "bus_route":
    case "big_bang":
    case "world_map_animated":
    case "cultural_symbols":
    case "music_instruments":
    case "language_bubbles":
    case "food_world":
    case "virus_structure":
    case "immune_response":
    case "blood_cells":
    case "cause_effect":
    case "pros_cons":
    case "leadership_pyramid":
    case "life_cycle": {
      const advances = firedActions.filter((a) => a.type === "advance");
      const steps = (props.steps as string[]) || (props.layers as string[]) || (props.items as string[]) || ["Step 1", "Step 2", "Step 3"];
      const activeIdx = Math.min(advances.length, steps.length - 1);
      props.activeIndex = activeIdx;
      props.completedIndices = Array.from({ length: activeIdx }, (_, i) => i);
      break;
    }

    case "coordinate_plane":
    case "line_graph": {
      const progress = Math.min(1, localTime / 3);
      props.flowProgress = progress;
      break;
    }

    case "gauge_meter":
    case "angle_protractor": {
      const duration = node.durationSeconds || 6;
      const progress = Math.min(1, localTime / duration);
      if (typeof props.value !== "number") {
        props.value = progress;
      } else {
        props.value = Math.max(0, Math.min(1, props.value));
      }
      break;
    }

    case "thermometer_bar":
    case "bar_chart":
    case "fraction_visual":
    case "revenue_chart":
    case "growth_curve":
    case "rocket_launch":
    case "pulley_system":
    case "inclined_plane":
    case "trophy_cup":
    case "solar_panel":
    case "battery_cell":
    case "car_dashboard":
    case "dinosaur_skeleton":
    case "whale_dive":
    case "spider_web":
    case "train_system":
    case "submarine_dive":
    case "hot_air_balloon":
    case "flower_bloom":
    case "root_network":
    case "pyramid_build": {
      const duration2 = node.durationSeconds || 6;
      const progress2 = Math.min(1, localTime / duration2);
      props.fillPercent = progress2 * 100;
      props.animationProgress = progress2;
      break;
    }

    case "wave_pattern":
    case "pendulum_swing":
    case "spring_force":
    case "wave_interference":
    case "flag_banner":
    case "bird_flight":
    case "lightning_storm":
    case "gas_particles":
    case "pollen_drift":
    case "moss_spores":
    case "skyscraper_sway":
    case "dome_pressure":
    case "sailboat_wind":
    case "airplane_cross":
    case "office_desk":
    case "playground":
    case "park_bench":
    case "bedroom_scene":
    case "gravity_well":
    case "orbital_mechanics":
    case "tidal_forces":
    case "magnetic_field_lines":
    case "electromagnetic_wave":
    case "centripetal_force":
    case "traditional_dance":
    case "festival_lanterns":
    case "bacteria_colony":
    case "neuron_firing":
    case "dna_replication":
    case "walking_character":
    case "running_character":
    case "jumping_character":
    case "waving_character":
    case "sitting_reading":
    case "pointing_teacher":
    case "dancing_character":
    case "swimming_character":
    case "time_flow":
    case "innovation_spark":
    case "idea_lightbulb":
    case "thumbs_up":
    case "question_doubt":
    case "happy_smiley":
    case "love_hearts":
    case "celebration_confetti":
    case "thinking_cloud":
    case "surprise_wow":
    case "single_girl":
    case "single_boy":
    case "handshake_deal":
    case "crowd_scene":
    case "couple_love":
    case "character_walk_cycle":
    case "dialogue_bubble":
    case "split_screen_compare": {
      props.phase = localTime * 1.5;
      props.amplitude = 0.3 + Math.sin(localTime * 0.4) * 0.2;
      break;
    }

    case "atom_orbital":
    case "solar_system":
    case "dna_helix":
    case "rotating_globe":
    case "compass_rose":
    case "gear_mechanism":
    case "clock_face":
    case "satellite_orbit":
    case "galaxy_spiral":
    case "asteroid_belt":
    case "gear_train":
    case "wind_turbine":
    case "bicycle_mechanics":
    case "helicopter_rotor":
    case "windmill_spin":
    case "lighthouse_beam":
    case "molecule_3d":
    case "crystal_lattice":
    case "dice_roll":
    case "rubik_cube":
    case "car_driving":
    case "truck_hauling":
    case "motorcycle_lean":
    case "bicycle_ride":
    case "globe_with_pins":
    case "teamwork_gears": {
      props.rotation = (localTime * 45) % 360;
      props.animationProgress = (localTime % 4) / 4;
      break;
    }

    case "venn_diagram":
    case "balance_scale":
    case "matrix_grid":
    case "stacked_bars":
    case "pie_segments":
    case "supply_demand":
    case "market_share":
    case "target_bullseye": {
      const advances3 = firedActions.filter((a) => a.type === "advance");
      if (advances3.length > 0) {
        props.activeIndex = advances3.length - 1;
        props.completedIndices = Array.from({ length: advances3.length - 1 }, (_, i) => i);
      }
      break;
    }

    case "hex_grid":
    case "tree_diagram":
    case "constellation_map": {
      const advances4 = firedActions.filter((a) => a.type === "advance");
      props.revealCount = advances4.length;
      break;
    }

    case "cell_diagram":
    case "heart_chambers":
    case "layered_cross":
    case "microscope_view":
    case "chemical_equation":
    case "volcano_cross_section":
    case "magnet_field":
    case "skeleton_system":
    case "brain_diagram":
    case "eye_anatomy":
    case "tooth_cross_section":
    case "muscle_fiber":
    case "telescope_view":
    case "world_map":
    case "continent_outline":
    case "city_grid":
    case "lightning_bolt":
    case "shield_badge":
    case "magnifying_glass":
    case "key_lock":
    case "light_bulb":
    case "mountain_landscape":
    case "castle_fortress":
    case "ancient_monument":
    case "house_blueprint":
    case "sports_field":
    case "fish_anatomy":
    case "black_hole":
    case "nebula_cloud":
    case "supernova_explosion":
    case "comet_trail":
    case "exoplanet_transit":
    case "pulsar_spin":
    case "dark_matter_web":
    case "rocket_launch_detailed":
    case "spaceship_warp":
    case "yacht_sailing": {
      if (firedActions.some((a) => a.type === "animate_flow")) {
        props.flowProgress = (localTime % 4) / 4;
      }
      // Note: coordinate_plane / line_graph are handled in their own earlier case.
      const advances5 = firedActions.filter((a) => a.type === "advance");
      if (advances5.length > 0) {
        props.activeIndex = advances5.length - 1;
      }
      break;
    }

    // ─── flowProgress-only group (new) ───────────────────────────────
    case "maze_path":
    case "projectile_arc":
    case "free_fall":
    case "cell_division":
    case "protein_folding":
    case "growth_chart": {
      const duration6 = node.durationSeconds || 6;
      props.flowProgress = Math.min(1, localTime / duration6);
      break;
    }

    default:
      break;
  }

  return props;
}

// ─── Guide props builder ─────────────────────────────────────────────
function buildGuideProps(node: FlowNode): Record<string, unknown> {
  const guide = node.guide;
  return {
    emotion: guide?.emotion || "explaining",
    pointDirection: guide?.action === "point" ? "left" : "none",
    x: 716,
    y: 404,
    scale: 1.05,
  };
}

// ─── Main App ────────────────────────────────────────────────────────
const App: React.FC = () => {
  const [view, setView] = React.useState<AppView>("landing");
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [time, setTime] = React.useState(0);
  const [duration, setDuration] = React.useState(0);
  const [currentNode, setCurrentNode] = React.useState<FlowNode | null>(null);
  const [currentNodeIndex, setCurrentNodeIndex] = React.useState(-1);
  const [flow, setFlow] = React.useState<Flow | null>(null);
  const [_errorMsg, setErrorMsg] = React.useState("");
  const [topic, setTopic] = React.useState("");
  // Anivaze v2: the style chosen on the landing screen (used for the next generation)…
  const [selectedStyle] = React.useState<VisualStyle>(DEFAULT_VISUAL_STYLE);
  // …and the style currently applied to the player. Restyling this is FREE (re-render
  // only — no AI/TTS/network), which is the core v2 differentiator.
  const [renderStyle, setRenderStyle] = React.useState<VisualStyle>(DEFAULT_VISUAL_STYLE);
  // v3 style-skin: drives skin-aware scenes/actors (the new style system).
  const [skinId, setSkinId] = React.useState<SkinId>("studio");
  // Elapsed seconds while generating — keeps the long (~30–60s) AI wait honest.
  const [loadingSecs, setLoadingSecs] = React.useState(0);
  // Editable storyboard: the generated scenes the user can tweak before making the video.
  const [editScenes, setEditScenes] = React.useState<FlowNode[]>([]);
  const pendingFlowRef = React.useRef<Flow | null>(null);
  // Real spoken narration (Web Speech API) + the avatar's live talking state.
  const [voiceOn, setVoiceOn] = React.useState(isSpeechSupported());
  const [isSpeaking, setIsSpeaking] = React.useState(false);
  const spokenNodeIdRef = React.useRef<string | null>(null);

  const engineRef = React.useRef<TimelineEngine | null>(null);
  const progressRef = React.useRef<ProgressTracker | null>(null);
  const executorRef = React.useRef<CommandExecutor | null>(null);
  const audioRef = React.useRef<AudioController | null>(null);
  const syncRef = React.useRef<SyncManager | null>(null);
  const clientRef = React.useRef<OrchestratorClient | null>(null);
  const flowRef = React.useRef<Flow | null>(null);
  const timeRef = React.useRef(0);
  const prevNodeIdRef = React.useRef<string | null>(null);

  // Suppress unused-var lint for errorMsg (used in error view)
  void _errorMsg;

  // ─── Initialize backend client ─────────────────────────────────────
  React.useEffect(() => {
    clientRef.current = new OrchestratorClient({
      baseUrl: API_BASE_URL,
      userId: API_USER_ID,
    });
  }, []);

  // ─── Initialize core engine (once) ─────────────────────────────────
  React.useEffect(() => {
    const engine = new TimelineEngine({
      onPlay: () => {
        setIsPlaying(true);
        progressRef.current?.play(timeRef.current);
        syncRef.current?.onPlay();
      },
      onPause: () => {
        setIsPlaying(false);
        progressRef.current?.pause(timeRef.current);
        syncRef.current?.onPause();
      },
      onTick: (t) => {
        timeRef.current = t;
        setTime(t);
        progressRef.current?.update(t);
        executorRef.current?.update(t);
        syncRef.current?.onTick(t);
      },
      onComplete: () => {
        setIsPlaying(false);
        progressRef.current?.pause(timeRef.current);
        syncRef.current?.onPause();
      },
    });
    engineRef.current = engine;
    progressRef.current = new ProgressTracker();

    const audio = new AudioController();
    audioRef.current = audio;
    syncRef.current = new SyncManager(audio);

    const executor = new CommandExecutor({
      onNodeStart: (node) => setCurrentNode(node),
      onNodeComplete: () => setCurrentNode(null),
    });
    executorRef.current = executor;

    return () => engine.stop();
  }, []);

  // ─── Seek helper (defined before hooks that reference it) ──────────
  const handleSeek = React.useCallback((seconds: number) => {
    cancelSpeech();
    setIsSpeaking(false);
    spokenNodeIdRef.current = null;
    progressRef.current?.seek(seconds);
    engineRef.current?.seek(seconds);
    syncRef.current?.onSeek(seconds);
    if (executorRef.current) {
      executorRef.current.reset();
      const activeFlow = flowRef.current;
      if (activeFlow) executorRef.current.loadFlow(activeFlow);
      executorRef.current.update(seconds);
    }
  }, []);

  // ─── Load flow into all subsystems ─────────────────────────────────
  React.useEffect(() => {
    if (!flow) return;
    flowRef.current = flow;
    cancelSpeech();
    setIsSpeaking(false);
    spokenNodeIdRef.current = null;
    // Apply the style the flow was generated with (falls back to the landing choice).
    setRenderStyle(flow.metadata.visualStyle ?? selectedStyle);

    const engine = engineRef.current;
    const tracker = progressRef.current;
    const executor = executorRef.current;
    const sync = syncRef.current;
    if (!engine || !tracker || !executor || !sync) return;

    setCurrentNode(null);
    setCurrentNodeIndex(-1);
    setTime(0);
    timeRef.current = 0;
    prevNodeIdRef.current = null;

    const total = flow.nodes.reduce((sum, n) => sum + n.durationSeconds, 0);
    setDuration(total);
    engine.loadFlow(flow);
    tracker.loadFlow(flow);
    executor.loadFlow(flow);
    sync.loadFlow(flow);

    // Try to restore progress from backend
    const client = clientRef.current;
    if (client) {
      void client
        .loadProgress(flow.flowId)
        .then((p) => {
          if (!p) return;
          progressRef.current?.restore({
            completedSeconds: p.completedSeconds,
            activeSeconds: p.activeSeconds,
          });
          handleSeek(p.completedSeconds);
        })
        .catch(() => {
          /* Best-effort */
        });
    }

    setView("playing");
  }, [flow, handleSeek]);

  // ─── Track current node index + transition animation ───────────────
  React.useEffect(() => {
    if (!flow || !currentNode) {
      setCurrentNodeIndex(-1);
      return;
    }
    const idx = flow.nodes.findIndex((n) => n.id === currentNode.id);
    setCurrentNodeIndex(idx);
    // The per-scene eased entrance/exit (computeSceneMotion) now carries the
    // transition, so the old white flash is gone. Just track the current node.
    prevNodeIdRef.current = currentNode.id;
  }, [currentNode, flow]);

  // ─── Periodic progress save ────────────────────────────────────────
  React.useEffect(() => {
    const client = clientRef.current;
    if (!client) return;
    let stopped = false;
    let inFlight: Promise<void> | null = null;
    const interval = window.setInterval(() => {
      if (stopped || inFlight) return;
      const activeFlow = flowRef.current;
      const progress = progressRef.current?.snapshot();
      if (!activeFlow || !progress) return;
      inFlight = client
        .saveProgress({
          flowId: activeFlow.flowId,
          completedSeconds: progress.completedSeconds,
          activeSeconds: progress.activeSeconds,
        })
        .catch(() => {})
        .finally(() => {
          inFlight = null;
        });
    }, 2000);
    return () => {
      stopped = true;
      window.clearInterval(interval);
    };
  }, []);

  // ─── Keyboard controls ─────────────────────────────────────────────
  React.useEffect(() => {
    if (view !== "playing") return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.code === "Space" || e.key === " ") {
        e.preventDefault();
        isPlaying
          ? engineRef.current?.pause()
          : engineRef.current?.play();
        return;
      }
      if (e.code === "ArrowLeft") {
        e.preventDefault();
        handleSeek(Math.max(0, timeRef.current - 5));
        return;
      }
      if (e.code === "ArrowRight") {
        e.preventDefault();
        handleSeek(Math.min(duration, timeRef.current + 5));
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [view, isPlaying, duration, handleSeek]);

  // ─── Loading elapsed-time ticker ───────────────────────────────────
  React.useEffect(() => {
    if (view !== "loading") return;
    setLoadingSecs(0);
    const id = window.setInterval(() => setLoadingSecs((s) => s + 1), 1000);
    return () => window.clearInterval(id);
  }, [view]);

  // ─── Spoken narration (Web Speech API) ─────────────────────────────
  // Speak each node's narration as it becomes active during playback; the
  // avatar's mouth syncs to onStart/onEnd.
  const speakingNodeId = currentNode?.id ?? null;
  React.useEffect(() => {
    if (view !== "playing" || !voiceOn || !isPlaying) return;
    const text = currentNode?.narrationText;
    if (!text || !speakingNodeId) return;
    if (spokenNodeIdRef.current === speakingNodeId) return; // already on this node
    spokenNodeIdRef.current = speakingNodeId;
    speak(text, { onStart: () => setIsSpeaking(true), onEnd: () => setIsSpeaking(false) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speakingNodeId, isPlaying, voiceOn, view]);

  // Pause/resume the voice with the player.
  React.useEffect(() => {
    if (!isSpeechSupported()) return;
    if (isPlaying) resumeSpeech();
    else pauseSpeech();
  }, [isPlaying]);

  // Stop the voice when muted or when leaving the player.
  React.useEffect(() => {
    if (!voiceOn || view !== "playing") {
      cancelSpeech();
      setIsSpeaking(false);
    }
  }, [voiceOn, view]);

  // Cancel any speech on unmount.
  React.useEffect(() => () => cancelSpeech(), []);

  // ─── Actions ───────────────────────────────────────────────────────
  const handleTopicSubmit = async (submittedTopic: string) => {
    setTopic(submittedTopic);
    setView("loading");
    setErrorMsg("");
    // Real Workers-AI generation (planner + per-node narration) can take ~30–60s
    // on the free tier. Give it room; only abort if truly stuck.
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 90000);
    try {
      const resp = await fetch(
        `${API_BASE_URL}/generate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-user-id": API_USER_ID,
          },
          body: JSON.stringify({
            topic: submittedTopic,
            complexityLevel: "beginner",
            humorLevel: 1,
            visualStyle: selectedStyle,
          }),
          signal: controller.signal,
        },
      );
      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        throw new Error(errData.error || `Server error: ${resp.status}`);
      }
      const data = await resp.json();
      if (data.flow) {
        // Show the editable storyboard first — the user can tweak/add/reorder
        // scenes before we make the video.
        pendingFlowRef.current = data.flow;
        setEditScenes(data.flow.nodes);
        setView("storyboard");
        return;
      }
      throw new Error("No flow returned from server");
    } catch (err: any) {
      console.error("Generate failed:", err);
      const raw = err?.name === "AbortError"
        ? "The generator didn't respond (backend may be offline). Try the offline demo to preview styles."
        : err.message || "Failed to generate explanation. Please try again.";
      // No backend jargon: "Planner returned invalid plan" / "Server error: 500"
      // mean nothing to a viewer — name the problem and the recovery instead.
      const msg = /invalid plan|Server error: \d+/i.test(raw)
        ? "The AI produced an unusable draft — this is usually temporary. Press Try Again, or rephrase the topic."
        : raw;
      setErrorMsg(msg);
      setView("error");
    } finally {
      window.clearTimeout(timeout);
    }
  };

  // Load a fully offline sample flow — no backend, no AI. Lets you test the
  // style picker + live restyle immediately.
  const handleLoadDemo = () => {
    engineRef.current?.stop();
    setTopic("Style demo");
    setErrorMsg("");
    setRenderStyle(selectedStyle);
    setFlow(withVoiceTiming(buildDemoFlow(selectedStyle)));
  };

  // Make the video from the (possibly edited) storyboard scenes.
  const handleGenerateFromScenes = () => {
    const base = pendingFlowRef.current;
    if (!base || editScenes.length === 0) return;
    const total = editScenes.reduce((sum, n) => sum + (n.durationSeconds || 8), 0);
    const finalFlow: Flow = {
      ...base,
      nodes: editScenes,
      metadata: { ...base.metadata, estimatedDurationSeconds: total },
    };
    setRenderStyle(base.metadata.visualStyle ?? selectedStyle);
    setFlow(withVoiceTiming(finalFlow));
  };

  const handleBackToLanding = () => {
    engineRef.current?.stop();
    cancelSpeech();
    setIsSpeaking(false);
    spokenNodeIdRef.current = null;
    setView("landing");
    setFlow(null);
    setCurrentNode(null);
    setTime(0);
    setIsPlaying(false);
  };

  const handleCtaAction = (action: string) => {
    if (!topic) return;
    let nextTopic = topic;
    if (action === "Solve more problems") {
      nextTopic = `${topic} practice problems`;
    } else if (action === "See example problems") {
      nextTopic = `${topic} worked examples`;
    } else if (action === "Explore subtopics") {
      nextTopic = `${topic} subtopics`;
    }
    handleTopicSubmit(nextTopic);
  };

  // ─── Compute rendering state ───────────────────────────────────────
  const activeFlow = flow;
  const nodeInfo = activeFlow ? getNodeAtTime(activeFlow, time) : null;
  const activeNode = nodeInfo?.node ?? currentNode;
  const localTime = nodeInfo?.localTime ?? 0;
  const nodeIndex = nodeInfo?.index ?? currentNodeIndex;
  const templateId = activeNode?.template;
  const TemplateRender = templateId ? getTemplate(templateId) : undefined;
  const GuideRender = getTemplate("guide_actor");
  const styleSpec = getStyleSpec(renderStyle);
  const templateProps = activeNode
    ? {
        ...buildTemplateProps(activeNode, localTime),
        ...(styleSpec.accentColor ? { accentColor: styleSpec.accentColor } : {}),
      }
    : {};
  // Real eased entrance → radial reveal → exit, applied at the group level so it
  // works on every template (replaces the old meaningless constant zoom + flash).
  const sceneMotion = activeNode ? computeSceneMotion(localTime, activeNode.durationSeconds) : null;
  const guideProps = activeNode
    ? {
        ...buildGuideProps(activeNode),
        t: localTime,
        // Sync the mouth to the real voice when it's on; otherwise move it while
        // narration is "active" so the mascot still looks like it's speaking.
        talking: voiceOn ? isSpeaking : isPlaying && !!activeNode?.narrationText,
      }
    : {};
  const narrationText = activeNode?.narrationText ?? undefined;
  const nodeTitle = activeNode
    ? (activeNode.template?.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase()) ?? undefined)
    : undefined;
  const ctaOptions = (activeNode?.props as any)?.ctaOptions as string[] | undefined;
  const ctaTitle = (activeNode?.props as any)?.ctaTitle as string | undefined;
  const showCta = !!ctaOptions && nodeIndex === (activeFlow?.nodes.length ?? 0) - 1;

  // ─── Render views ──────────────────────────────────────────────────

  if (view === "landing") {
    return (
      <div className="app">
        <TopicInput
          onSubmit={handleTopicSubmit}
          isLoading={false}
          skin={skinId}
          onSkinChange={setSkinId}
          onDemo={handleLoadDemo}
        />
      </div>
    );
  }

  if (view === "loading") {
    return (
      <div className="app">
        <div className="loading-container">
          <div className="loading-spinner" />
          <p className="loading-text">
            Generating explanation for &ldquo;{topic}&rdquo;&hellip;
          </p>
          <p className="loading-subtext">
            Real AI is planning the visuals and writing narration — this can take
            up to a minute.{loadingSecs > 0 ? ` (${loadingSecs}s)` : ""}
          </p>
        </div>
      </div>
    );
  }

  if (view === "error") {
    return (
      <div className="app">
        <div className="error-container">
          <div className="error-icon">!</div>
          <h2 className="error-title">Something went wrong</h2>
          <p className="error-message">
            {_errorMsg ||
              "Failed to generate the explanation. Please try again."}
          </p>
          <button className="error-btn" onClick={handleBackToLanding}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // ─── Storyboard editor view ────────────────────────────────────────
  if (view === "storyboard") {
    return (
      <div className="app">
        <StoryboardEditor
          topic={topic}
          scenes={editScenes}
          onChange={setEditScenes}
          onGenerate={handleGenerateFromScenes}
          onBack={handleBackToLanding}
        />
      </div>
    );
  }

  // ─── Playing view ──────────────────────────────────────────────────
  return (
    <div className="app">
      <div className="player-container">
        {/* Header */}
        <div className="player-header">
          <div className="player-header-left">
            <button
              className="player-logo"
              onClick={handleBackToLanding}
              title="Back to home"
            >
              <svg width="28" height="28" viewBox="0 0 48 48" fill="none">
                <rect width="48" height="48" rx="10" fill="#6366f1" />
                <path
                  d="M14 16L24 12L34 16V28L24 32L14 28V16Z"
                  fill="#818cf8"
                  stroke="#c7d2fe"
                  strokeWidth="1.5"
                />
                <circle cx="24" cy="22" r="3" fill="#e0e7ff" />
              </svg>
              <span className="player-logo-text">LearnEngine</span>
            </button>
            {topic && (
              <span className="player-topic-badge">{topic}</span>
            )}
          </div>
          <div className="player-header-right">
            {isSpeechSupported() && (
              <button
                className="voice-btn"
                onClick={() => setVoiceOn((v) => !v)}
                title={voiceOn ? "Mute narration" : "Unmute narration"}
              >
                {voiceOn ? "🔊 Voice" : "🔇 Muted"}
              </button>
            )}
            {pendingFlowRef.current && editScenes.length > 0 && (
              <button className="edit-scenes-btn" onClick={() => setView("storyboard")} title="Back to the scene editor">
                ✏ Edit scenes
              </button>
            )}
            <span className="restyle-label">Style</span>
            <select
              className="restyle-select"
              value={skinId}
              onChange={(e) => setSkinId(e.target.value as SkinId)}
              title="Illustration style — re-skins the whole video instantly, free"
            >
              {SKIN_OPTIONS.map((s) => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Canvas */}
        <div className="canvas-wrapper">
          <CanvasRoot width={800} height={450}>
            {/* Active style's filters/patterns/gradients (Anivaze v2 style engine) */}
            <defs>
              {styleSpec.renderDefs()}
              {sceneMotion?.revealRadius != null && (
                <clipPath id="scene-reveal">
                  <circle cx={400} cy={225} r={sceneMotion.revealRadius} />
                </clipPath>
              )}
            </defs>
            <LayerManager
              background={styleSpec.renderBackground(800, 450)}
              main={
                TemplateRender && activeNode ? (
                  <SkinProvider skin={getSkin(skinId)}>
                    <g
                      transform={`translate(0 ${sceneMotion?.translateY ?? 0}) translate(400 225) scale(${sceneMotion?.scale ?? 1}) translate(-400 -225)`}
                      opacity={sceneMotion?.opacity ?? 1}
                      clipPath={sceneMotion?.revealRadius != null ? "url(#scene-reveal)" : undefined}
                    >
                      {TemplateRender(templateProps)}
                    </g>
                  </SkinProvider>
                ) : (
                  <g>
                    <text
                      x="400"
                      y="225"
                      fill="#475569"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fontSize="16"
                      fontFamily="system-ui, sans-serif"
                    >
                      Press play to begin
                    </text>
                  </g>
                )
              }
              overlay={
                <g>
                  {/* Guide mascot — always present, talks while narration plays */}
                  {GuideRender && activeNode && GuideRender(guideProps)}
                </g>
              }
            />
          </CanvasRoot>
        </div>

        {/* Controls */}
        <PlayerControls
          isPlaying={isPlaying}
          currentTimeSeconds={time}
          durationSeconds={duration}
          currentNodeIndex={nodeIndex}
          totalNodes={activeFlow?.nodes.length ?? 0}
          nodeTitle={nodeTitle}
          onPlay={() => engineRef.current?.play()}
          onPause={() => engineRef.current?.pause()}
          onSeek={handleSeek}
        />

        {/* Narration */}
        <NarrationPanel
          text={narrationText}
          isActive={isPlaying && !!activeNode}
          nodeTitle={nodeTitle}
        />

        {showCta && (
          <div className="cta-panel">
            <div className="cta-title">{ctaTitle ?? "What would you like next?"}</div>
            <div className="cta-actions">
              {ctaOptions.map((label) => (
                <button
                  key={label}
                  className="cta-btn"
                  onClick={() => handleCtaAction(label)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Root: landing → studio (#app) · legal (#terms/#privacy) · legacy ──
const Root: React.FC = () => {
  const [hash, setHash] = React.useState(() => (typeof window !== "undefined" ? window.location.hash : ""));
  React.useEffect(() => {
    const onHash = () => setHash(window.location.hash);
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  const h = hash.replace("#", "");
  if (h.startsWith("legacy")) return <App />;
  if (h.startsWith("terms")) return <LegalPage doc="terms" onBack={() => { window.location.hash = ""; }} />;
  if (h.startsWith("privacy")) return <LegalPage doc="privacy" onBack={() => { window.location.hash = ""; }} />;
  if (h.startsWith("app") || h.startsWith("v4")) {
    return <Phase1Preview onExit={() => { window.location.hash = "legacy"; }} />;
  }
  // The landing page is the front door; the studio lives at #app.
  return <Landing onStart={() => { window.location.hash = "app"; }} />;
};

ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement,
).render(
  <React.StrictMode>
    <ErrorBoundary>
      <Root />
    </ErrorBoundary>
  </React.StrictMode>,
);
